import { DateTime } from '@mode-switch/express';
import { From, View } from '@mode-switch/model';
import { AppState, QueryState, TableState, type ClientState } from '@mode/legacy/types-app-state';
import { ChartTypes } from '@mode/shared/contract-common';
import { Switch } from '@mode/shared/util-switch';
import { List, Map } from 'immutable';
import { Selector, createSelector, createSelectorCreator, defaultMemoize } from 'reselect';
import { getEncodedChartInNormalizedChartArray } from '../lib/chart-helpers';
import { NotebookSelectors } from './notebook.selectors';

function isEncodedChartEqual<T>(a: T, b: T): boolean {
  const chart1 = (<any>a) as ChartTypes.EncodedChart;
  const chart2 = (<any>b) as ChartTypes.EncodedChart;

  if (!chart1 || !chart2) {
    return false;
  }

  if (chart1 === chart2) {
    return true;
  }

  return (
    chart1.chartType === chart2.chartType &&
    chart1.colorPaletteToken === chart2.colorPaletteToken &&
    chart1.encoding === chart2.encoding &&
    chart1.queryToken === chart2.queryToken &&
    chart1.format === chart2.format &&
    chart1.fieldFormats === chart2.fieldFormats &&
    chart1.title === chart2.title &&
    chart1.v1View === chart2.v1View &&
    chart1.version === chart2.version
  );
}

const createEncodedChartSelector = createSelectorCreator(defaultMemoize, isEncodedChartEqual);

export namespace SelectedReportSelectors {
  export const getChartModels = (state: AppState) => state.client.selectedReport.chartModels;
  export const getTables = (state: AppState) => state.client.selectedReport.tables;
  // TODO: Remove this selector in favor of the one in chart.selectors
  export const getNormalizedChartArray = (state: AppState) => state.chart;
  export const getQueries = (state: AppState) => state.client.selectedReport.queries;

  export function createQueryResourceSelector(queryToken: string) {
    return (state: AppState) => state.client.selectedReport.queries.getRecord(queryToken)?.resource;
  }

  export const getQueryAttrs = (state: AppState) => state.client.selectedReport.switchAttrsByQuery;

  export const getQueryRuns = (state: AppState) => state.client.selectedReport.queryRuns;

  export const reportContextSelector = (state: AppState): ClientState['reportContext'] => state.client.reportContext;

  export const getModel = createSelector(getQueryAttrs, reportContextSelector, (switchAttrs, reportContext) => {
    const weekStart = DateTime.weekStart(reportContext.settings?.['week_start']);
    const yearStart = DateTime.yearStart(reportContext.settings?.['year_start']);

    const views = Object.keys(switchAttrs).map((queryToken) => {
      const queryAttrs = switchAttrs[queryToken];
      if (!queryAttrs) {
        return;
      }

      const { columns, calcs } = queryAttrs;
      if (!columns) {
        return;
      }

      let attrs = [...columns];
      if (calcs) {
        attrs = [...columns, ...calcs];
      }

      return View.make(From.table(queryAttrs.from), queryToken, {
        weekStart,
        yearStart,
        attrs,
      });
    });

    return Switch.Models.make(null, views);
  });

  export const getColumnModel = createSelector(getQueryAttrs, reportContextSelector, (switchAttrs, reportContext) => {
    const weekStart = DateTime.weekStart(reportContext.settings?.['week_start']);
    const yearStart = DateTime.yearStart(reportContext.settings?.['year_start']);

    const views = Object.keys(switchAttrs).map((queryToken) => {
      const queryAttrs = switchAttrs[queryToken];
      if (!queryAttrs) {
        return;
      }

      const { columns } = queryAttrs;
      if (!columns) {
        return;
      }

      const attrs = [...columns];
      return View.make(From.table(queryAttrs.from), queryToken, {
        weekStart,
        yearStart,
        attrs,
      });
    });

    return Switch.Models.make(null, views);
  });

  export const getLayout = (state: AppState) => state.client.selectedReport.reportLayout;
  export const getTheme = (state: AppState) => state.client.selectedReport.theme;

  export const hasRecentReportRun = (state: AppState) => state.client.selectedReport.hasRecentReportRun;

  export const getRecentReportRun = (state: AppState) => state.client.selectedReport.recentReportRun;

  export const getRecentReportRunToken = (state: AppState) =>
    state.client.selectedReport.recentReportRun && state.client.selectedReport.recentReportRun.lastReportRunId;

  export const getReportRunToken = (state: AppState) =>
    state.client.selectedReport.reportRun &&
    state.client.selectedReport.reportRun.resource &&
    state.client.selectedReport.reportRun.resource.token;

  export const getReportRunState = (state: AppState) =>
    state.client.selectedReport.reportRun && state.client.selectedReport.reportRun.state;

  export const getCalcEditorModal = (state: AppState) =>
    state.client.selectedReport && state.client.selectedReport.calcEditorModal;

  export const getNormalizedCharts = createSelector(getNormalizedChartArray, (charts) =>
    charts.tokens.toList().map((token) => getEncodedChartInNormalizedChartArray(charts, token))
  );

  export const getHasNotebook = createSelector(
    (state: AppState) => state.client.selectedReport.report,
    (report) => report != null && report.hasLink('python_notebook')
  );

  export const getNotebookLoading = createSelector(
    (state: AppState) => state.client.selectedReport.report,
    getHasNotebook,
    NotebookSelectors.selectedNotebookToken,
    (report, hasNotebook, selectedNotebookToken) => report == null || (hasNotebook && selectedNotebookToken == null)
  );

  export const getChartTokensByQueryMap = (state: AppState) => state.client.selectedReport.chartsByQuery;
  export const getChartsByQueries = createSelector(
    getChartTokensByQueryMap,
    getNormalizedCharts,
    (chartMap, charts): Map<string, List<ChartTypes.EncodedChart>> => {
      const result: { [queryToken: string]: List<ChartTypes.EncodedChart> } = {};

      chartMap.forEach((val, key) => {
        result[key] = charts.filter((c) => val.has(c.token));
      });

      return Map<List<ChartTypes.EncodedChart>>(result);
    }
  );

  function checkQueryDirty(query: QueryState) {
    if (query.resource != null) {
      // not checking name because query is saved on rename
      return (
        query.last_run_id && // If query hasn't been run before, it can't be dirty
        (query.raw_query !== query.resource.raw_query ||
          getDatasourceId(query.data_source_id) !== getDatasourceId(query.resource.data_source_id))
      );
    }

    return false;
  }

  function getDatasourceId(datasource): String {
    return datasource != null && typeof datasource !== 'undefined' ? datasource.toString() : '';
  }

  // Note that this gets called a lot because queries are not normalized
  export const getEditorIsDirty = createSelector(
    getLayout,
    (state: AppState) => state.client.selectedReport.report && state.client.selectedReport.report.layout,
    (state: AppState) => state.client.selectedReport.report && state.client.selectedReport.report.last_run_at,
    getQueries,
    (layout, storedLayout, last_run_at, queries) => {
      return last_run_at != null && (layout !== storedLayout || queries.getRecords().some(checkQueryDirty));
    }
  );

  // Note that this gets called a lot because queries are not normalized
  export const getDatasetEditorIsDirty = createSelector(
    (state: AppState) => state.client.selectedReport.report && state.client.selectedReport.report.last_run_at,
    getQueries,
    (last_run_at, queries) => {
      return last_run_at != null && queries.getRecords().some(checkQueryDirty);
    }
  );

  export function createIsDirtySelector(isDataset = false) {
    return isDataset ? getDatasetEditorIsDirty : getEditorIsDirty;
  }

  export const getNotebookVizTokens = (state: AppState) => state.client.selectedReport.notebookViz.tokens;

  export const getNotebookCellTokens = createSelector(
    getNotebookVizTokens,
    (state: AppState) => state.client.selectedReport.notebookViz.cellTokens,
    (vizTokens, cellTokens) => vizTokens.map((token) => cellTokens.get(token)).filter((token) => token != null)
  );

  export const isCancelingSelectedQueryRun = createSelector(
    (state: AppState) => state.reportEditor.selectedQueryToken,
    (state: AppState) => state.client.selectedReport.queryRuns,
    (queryToken, runs) => {
      const record = runs.getRecord(queryToken);

      if (record != null) {
        return record.cancelQueued;
      }

      return false;
    }
  );

  export const selectedQueryRunRecord = createSelector(
    (state: AppState) => state.reportEditor.selectedQueryToken,
    (state: AppState) => state.client.selectedReport.queryRuns,
    (queryToken, runs) => runs.getRecord(queryToken)
  );

  export const isSelectedQueryRunning = createSelector(selectedQueryRunRecord, (record) => {
    if (record != null) {
      return record.running || record.runQueued;
    }
    return false;
  });

  export const isSelectedQueryCancelled = createSelector(selectedQueryRunRecord, (record) => {
    if (record != null) {
      return record?.state === 'cancelled';
    }
    return false;
  });

  export const canCancelSelectedQueryRun = createSelector(
    (state: AppState) => state.reportEditor.selectedQueryToken,
    (state: AppState) => state.client.selectedReport.queryRuns,
    (queryToken, runs) => {
      const record = runs.getRecord(queryToken);

      // Need to check that the resource exists so the cancel can actually be executed
      if (record != null && record.resource != null) {
        return record.running && !record.cancelQueued;
      }

      return false;
    }
  );

  // Blocks until switch view exists for the given queryToken. Would be nice to break into two selectors with a combination
  // but that could be difficult to do nicely due to queryToken and run token being necessary to check the view
  export function createQueryRunTokenSelector(queryToken: string) {
    return createSelector(
      (state: AppState) => getQueryRuns(state).getRecord(queryToken),
      getModel,
      (run, model) => (run != null && Switch.Views.isFrom(model, queryToken, run.token) && run.token) || undefined
    );
  }

  export function createTableResourceSelector(token: string) {
    return createSelector(
      (state: AppState) => state.client.selectedReport.tables.getRecord(token).resource,
      (res) => res
    );
  }

  export const getTableTokensByQueryMap = (state: AppState) => state.client.selectedReport.tablesByQuery;
  export function createTableSelector(token: string): Selector<AppState, TableState> {
    return createSelector(
      (state: AppState) => state.client.selectedReport.tables.getRecord(token),
      (table) => table
    );
  }

  export function createFilteredDatasetSelector(queryToken: string) {
    return createSelector(
      (state: AppState) => state.transient.report.datasets.getRecord(queryToken),
      (state: AppState) => state.client.selectedReport.queryRuns.getRecord(queryToken),
      (dataset, run) => {
        if (run != null && ['pending', 'enqueued'].indexOf(run.state) < 0) {
          return dataset;
        }

        return null;
      }
    );
  }

  export namespace Parameterized {
    // Only used by legacy v1 charts inside ChartDesignerComponent
    const getEncodedChartUnmemoized = (state: AppState, chartToken: string) =>
      getEncodedChartInNormalizedChartArray(getNormalizedChartArray(state), chartToken);
    export const getEncodedChart = createEncodedChartSelector(getEncodedChartUnmemoized, (c) => c);
    export const getChartModel = createSelector(getEncodedChart, getChartModels, (chart, models) =>
      chart ? models.getRecord(chart.queryToken) : undefined
    );
  }
}
